/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import FileDropBox from './fileDropBox';
import AttachmentList from './attachmentList';
import ItemTypes from '../../constants/itemTypes';
import {
  fetchAttachments,
  fetchThumbnail,
  uploadAttachment,
  deleteAttachment,
  downloadUrl,
  setCounting
} from '../../actions/attachmentActions';

class Attachments extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      droppedFile: null,
      droppedFileUrl: ''
    };
    this.uploadFile = this.uploadFile.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.handleFileDrop = this.handleFileDrop.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
  }

  componentDidMount() {
    if (this.props.itemId) {
      this.props.fetchAttachments(this.props.itemId, this.props.type);
    }
  }

  shouldComponentUpdate(nextProps) {
    return (
      (this.props.attachmentList.attachments &&
        nextProps.attachmentList.attachments &&
        this.props.attachmentList.attachments.length !==
          nextProps.attachmentList.attachments.length) ||
      this.props.attachmentList.isUploading !==
        nextProps.attachmentList.isUploading ||
      this.props.attachmentList.isFetching !==
        nextProps.attachmentList.isFetching ||
      this.props.attachmentList.showLoading !==
        nextProps.attachmentList.showLoading
    );
  }

  componentDidUpdate(prevProps) {
    if (this.props.itemId !== prevProps.itemId) {
      this.props.fetchAttachments(this.props.itemId, this.props.type);
    }
  }

  getValidSizeFiles = droppedFiles =>
    Array.from(droppedFiles).filter(
      file => Math.round(file.size / 1000000) < 50
    );

  fileUploadMiddleware = droppedFiles => {
    const validSizeFiles = this.getValidSizeFiles(droppedFiles);
    const filesToUpload = this.filterAlreadyUploadedFiles(validSizeFiles);
    if (!this.props.isNew) this.props.setCounting(filesToUpload.length);
    filesToUpload.map(file => this.uploadFile(file));
    if (droppedFiles.length > validSizeFiles.length) {
      alert('File size must be less than 50 MB');
    }
  };

  filterAlreadyUploadedFiles = droppedFiles => {
    const { attachments } = this.props.attachmentList;
    const uploadedFileNames = attachments.map(
      attachment =>
        this.props.isNew ? attachment.droppedFile.name : attachment.short_name
    );
    const notUploadedFiles = file => !uploadedFileNames.includes(file.name);
    return droppedFiles.filter(notUploadedFiles);
  };

  uploadFile(file) {
    const reader = new FileReader();
    reader.onloadend = () => {
      this.setState(
        { droppedFileUrl: reader.result, droppedFile: file },
        () => {
          if (this.props.isNew) {
            const attachment = {
              droppedFile: this.state.droppedFile,
              droppedFileUrl: this.state.droppedFileUrl,
              type: this.props.type
            };
            this.props.addAttachment(attachment);
          } else {
            this.props.uploadAttachment(
              this.state.droppedFile,
              this.state.droppedFileUrl,
              this.props.itemId,
              this.props.type
            );
          }
        }
      );
    };
    if (file) {
      reader.readAsDataURL(file);
    } else {
      this.setState({ droppedFileUrl: '', droppedFile: null });
    }
  }

  handleFileUpload(event) {
    const droppedFiles = event.target.files;
    this.fileUploadMiddleware(droppedFiles);
    event.target.value = '';
  }

  handleFileDrop(item, monitor) {
    if (monitor) {
      const droppedFiles = monitor.getItem().files;
      this.fileUploadMiddleware(droppedFiles);
    }
  }

  handleDelete(attachmentId, index) {
    if (index > -1 && this.props.allowDelete) {
      this.props.removeAttachment(index);
    } else {
      this.props.deleteAttachment(attachmentId);
      this.props.fetchAttachments(this.props.itemId, this.props.type);
    }
  }

  render() {
    const {
      attachmentList,
      itemId,
      isNew,
      allowCreate,
      allowDelete
    } = this.props;
    const attachments = isNew
      ? attachmentList.attachments
      : attachmentList.attachments &&
        attachmentList.attachments.filter(
          attachment =>
            attachment.work_package_id === itemId ||
            attachment.task_id === itemId ||
            attachment.product_id === itemId
        );
    const list = attachmentList;
    list.attachments = attachments;
    return (
      <div className="attachments-container">
        <label htmlFor="attachments">Attachments</label>
        <div className="attachments-scroll">
        { allowCreate ?
          <FileDropBox
            accepts={ItemTypes.FILE}
            onDrop={this.handleFileDrop}
            onUpload={this.handleFileUpload}
            allowCreateAttachment={true}
          /> :
          null
        }
          {list.isUploading || list.isFetching ? <div id="mini-loader" /> : null}
          {list.attachments &&
            list.attachments.length > 0 && (
              <div className="attachments">
                <AttachmentList
                  list={list}
                  onDelete={this.handleDelete}
                  allowDelete={allowDelete}
                />
              </div>
            )}
        </div>
      </div>
    );
  }
}

Attachments.propTypes = {
  itemId: PropTypes.number,
  type: PropTypes.string.isRequired,
  allowCreate: PropTypes.bool.isRequired,
  allowDelete: PropTypes.bool.isRequired,
  attachmentList: PropTypes.object,
  fetchAttachments: PropTypes.func,
  fetchThumbnail: PropTypes.func,
  downloadUrl: PropTypes.func,
  uploadAttachment: PropTypes.func,
  deleteAttachment: PropTypes.func
};

const mapStateToProps = (state, ownProps) => ({
  attachmentList: ownProps.isNew
    ? {
        isUploading: false,
        preview: true,
        attachments: ownProps.attachmentList
      }
    : state.attachmentReducer
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      fetchAttachments,
      uploadAttachment,
      deleteAttachment,
      fetchThumbnail,
      downloadUrl,
      setCounting
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Attachments);
